import { StatusId, CommandeBCI, CommandeFormulaire, OperationRef } from '../types';
import ArticlesService, { Article } from '../../../services/articlesService';

// Derivations PURES de l'onglet Consultation PRESTATAIRE-BC. Aucune dependance React.
// Forme PRESTA : la commande WRAPPE l'operation (commande.operation.*) et porte ses
// lignes dans commande.articles (PAS commande.commandes[rang].articles, qui est la forme
// ACHETEUR). Gating 100% id-based (enum StatusId), jamais par libelle de statut.
//
// Copie autonome (et adaptee) des fonctions pures du gabarit acheteur
// SuiviOperationBCV2/domain/operationConsultation.ts. Volontairement dupliquee : la case
// presta ne doit importer aucun module de la case acheteur (regle d'autonomie).

// --- Articles existants (tableau financier presta) ----------------------------
// Forme d'une ligne PRESTA (commande.articles[]), cf. ConsultationBC.tsx:406-520 :
//   - catalogue       = item.catalogue_prix?.catalogue || item.catalogue
//   - prix unitaire   = item.article.prix_unitaire (prix negocie) sinon catalogue_prix.prix_a
//   - quantite        = item.article.quantity
//   - reference       = `${dept2} ${lotTech} ${code} ${rang}` depuis commande.operation.*
//   - paliers         = catalogue.seuil_a/seuil_b + catalogue_prix.prix_a/b/c
//   - catalogueId     = item.id_catalogue_prix?.toString() || item.id  (cle stable pour DELETE/PUT)
// On recalcule totalHT via ArticlesService.getPrixByQuantity (paliers degressifs).
interface RawCataloguePrix {
  prix_a?: string | number;
  prix_b?: string | number;
  prix_c?: string | number;
  catalogue?: RawCatalogue;
}

interface RawArticle {
  id?: number | string;
  prix_unitaire?: string | number;
  prix?: string | number;
  quantity?: string | number;
  quantite?: string | number;
  prix_total?: string | number;
  totalHT?: string | number;
}

interface RawCatalogue {
  id?: number | string;
  code?: string;
  nom?: string;
  description?: string;
  unite?: string;
  seuil_a?: number;
  seuil_b?: number;
  prix_a?: string | number;
  prix_b?: string | number;
  prix_c?: string | number;
  // Cote presta (onglet Offre), l'article aux prix negocies est NICHE sous le catalogue.
  article?: RawArticle;
}

interface RawCommandeArticle {
  id?: number | string;
  id_catalogue_prix?: number | string;
  quantite?: number | string;
  catalogue?: RawCatalogue;
  catalogue_prix?: RawCataloguePrix;
  article?: RawArticle;
}

const toNumber = (value: unknown): number => {
  const n = parseFloat(String(value ?? ''));
  return Number.isNaN(n) ? 0 : n;
};

const toQuantity = (value: unknown): number => {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isNaN(n) || n < 1 ? 1 : n;
};

export const formatCommandeArticles = (commande: CommandeBCI): Article[] => {
  const rawArticles = (commande.articles || []) as RawCommandeArticle[];
  const operation = commande.operation || {};
  const departementId = (operation.lot_departement?.departement?.id ?? '00')
    .toString()
    .padStart(2, '0');
  const lotTechniqueId = (operation.lot_technique?.id ?? '000').toString();
  const rang = commande.rang ?? 1;

  return rawArticles.map((item, index): Article => {
    const cataloguePrix = item.catalogue_prix;
    const catalogue = item.catalogue || cataloguePrix?.catalogue || {};
    // Cote presta, l'article aux prix negocies est niche sous le catalogue : item.catalogue.article
    // (cf. OffreBC.tsx:438,465-466). Fallback : item.article. C'EST LA CAUSE DES PRIX A 0 :
    // l'ancien chemin item.article.* etait vide dans l'onglet Offre.
    const articleData = item.catalogue?.article || item.article || {};

    // Prix unitaire : prix negocie de l'article, sinon prix catalogue (palier 1 / prix_a).
    const prix = toNumber(
      articleData.prix_unitaire ?? articleData.prix ?? catalogue.prix_a ?? cataloguePrix?.prix_a,
    );
    const quantite = toQuantity(articleData.quantity ?? articleData.quantite ?? item.quantite);
    const seuilA = catalogue.seuil_a || 5;
    const seuilB = catalogue.seuil_b || 10;
    const code = catalogue.code || item.id?.toString() || '';
    const reference = `${departementId} ${lotTechniqueId} ${code} ${rang}`;

    // Paliers degressifs (recalcul en panier inverse) : prix_a/b/c du catalogue, sinon catalogue_prix.
    const prixA = toNumber(catalogue.prix_a ?? cataloguePrix?.prix_a) || prix;
    const prixB = toNumber(catalogue.prix_b ?? cataloguePrix?.prix_b) || prix;
    const prixC = toNumber(catalogue.prix_c ?? cataloguePrix?.prix_c) || prix;

    const articleBase: Article = {
      // id unique pour le rendu (cle React) : article niche -> catalogue -> item -> index en
      // dernier recours (l'id reel est sous item.catalogue.article.id, comme les prix).
      id: (articleData.id ?? catalogue.id ?? item.id)?.toString() || `art-${index}`,
      // Cle stable pour DELETE/PUT /bdc/commande/{id}/catalogue/{catalogueId} : on prefere
      // id_catalogue_prix (memo db 'bdc catalogue_prix mismatch' : ne pas keyer sur catalogue.id).
      catalogueId:
        (item.id_catalogue_prix ?? catalogue.id ?? item.id ?? articleData.id)?.toString() || '',
      code: reference,
      nom: catalogue.nom || 'Article',
      description: catalogue.description || '',
      prix,
      unite: catalogue.unite || 'Unité',
      quantite,
      totalHT: 0,
      prixDegressifs: {
        palier1: { min: 1, max: seuilA, prix: prixA },
        palier2: { min: seuilA + 1, max: seuilB, prix: prixB },
        palier3: { min: seuilB + 1, prix: prixC },
      },
    };

    // Total : prefere le prix_total deja calcule par le serveur (article sauvegarde) ;
    // sinon recalcul par palier degressif (cas edition panier inverse).
    const serverTotal = toNumber(articleData.prix_total ?? articleData.totalHT);
    const totalHT =
      serverTotal > 0 ? serverTotal : ArticlesService.getPrixByQuantity(articleBase, quantite) * quantite;
    return { ...articleBase, totalHT };
  });
};

export const totalArticlesHT = (articles: Article[]): number =>
  articles.reduce((sum, a) => sum + a.totalHT, 0);

// --- Transferred (forme PRESTA) -----------------------------------------------
// Cote presta, le flag vit sur l'operation wrappee (commande.operation?.transferred),
// JAMAIS sur commande.transferred (ca c'est la forme acheteur). Cf. ConsultationBC.tsx:587.
export const isTransferred = (commande: CommandeBCI): boolean =>
  commande.operation?.transferred === true;

// --- Expiration (id-based + date) ---------------------------------------------
const isDateExpired = (dateStr?: string | null): boolean => {
  if (!dateStr) return false;
  const target = new Date(dateStr);
  return !Number.isNaN(target.getTime()) && Date.now() >= target.getTime();
};

export const isCommandeExpired = (commande: CommandeBCI, chronoExpired = false): boolean =>
  chronoExpired ||
  commande.status?.id === StatusId.Expire ||
  isDateExpired(commande.expire_at);

// --- Prestataire a deja agi (=> tout read-only) -------------------------------
// Reproduit par ID le legacy isSectionReadOnly l.2240-2246 (qui matchait des libelles).
// Statuts terminaux / en attente acheteur : Valide(7), Refuse(8), Expire(9), AttenteAnalyse(13).
const ACTED_STATUSES: ReadonlySet<number> = new Set<number>([
  StatusId.Valide, // 7
  StatusId.Refuse, // 8
  StatusId.Expire, // 9
  StatusId.AttenteAnalyse, // 13 = offre envoyee, en attente decision acheteur
]);

export const prestataireHasActed = (commande: CommandeBCI): boolean => {
  const sid = commande.status?.id;
  return sid !== undefined && ACTED_STATUSES.has(sid);
};

// --- Le presta doit-il accepter / refuser (panier normal) ? -------------------
// Panier NORMAL : non transferred, statut "A accepter" du rang (AAccepter(4) ou les
// statuts d'attente d'acceptation par rang 3/5/6), pas encore agi, non expire.
// Cf. ConsultationBC.tsx:3627-3661 (legacy matchait 'En consultation' / 'En attente de
// l'acceptation du rang X' par libelle -> ici par id).
const A_ACCEPTER_STATUSES: ReadonlySet<number> = new Set<number>([
  StatusId.AAccepter, // 4
  StatusId.AttenteAcceptationRang1, // 3
  StatusId.AttenteAcceptationRang2, // 5
  StatusId.AttenteAcceptationRang3, // 6
]);

export const canAccepterRefuser = (commande: CommandeBCI, chronoExpired = false): boolean => {
  if (isCommandeExpired(commande, chronoExpired)) return false;
  if (isTransferred(commande)) return false;
  if (prestataireHasActed(commande)) return false;
  const sid = commande.status?.id;
  return sid !== undefined && A_ACCEPTER_STATUSES.has(sid);
};

// --- Le presta peut-il editer le catalogue (panier inverse A chiffrer) ? -------
// Panier INVERSE : transferred + statut AChiffrer(11) + bon rang. En V2, l'edition
// reelle du catalogue + l'envoi de l'offre vivent dans l'onglet OFFRE (le legacy retourne
// null dans Consultation pour le formulaire d'articles en mode 'A chiffrer', l.2763-2768).
// Cette derivation reste exposee pour documenter la frontiere (et conditionner un eventuel
// tableau editable si on decidait de l'exposer en Consultation).
export const canPrestataireEditCatalogue = (
  commande: CommandeBCI,
  chronoExpired = false,
): boolean => {
  if (isCommandeExpired(commande, chronoExpired)) return false;
  if (!isTransferred(commande)) return false;
  return commande.status?.id === StatusId.AChiffrer;
};

// --- Bandeau de statut presta read-only (✓ / ✗ / ⏰) ---------------------------
// Reproduit ConsultationBC.tsx:3666-3695 (statuts terminaux) mais 100% par id.
export interface PrestataireStatusBanner {
  visible: boolean;
  variant: 'accepted' | 'refused' | 'expired' | 'waiting';
  message: string;
}

export const computePrestataireStatusBanner = (
  commande: CommandeBCI,
  chronoExpired = false,
): PrestataireStatusBanner => {
  const sid = commande.status?.id;

  if (isCommandeExpired(commande, chronoExpired)) {
    return { visible: true, variant: 'expired', message: '⏰ Opération expirée' };
  }
  if (sid === StatusId.Valide) {
    return { visible: true, variant: 'accepted', message: '✓ Offre acceptée' };
  }
  if (sid === StatusId.Refuse) {
    return { visible: true, variant: 'refused', message: '✗ Opération refusée' };
  }
  if (sid === StatusId.AttenteAnalyse) {
    return {
      visible: true,
      variant: 'waiting',
      message: "⏳ Offre envoyée, en attente de la décision de l'acheteur",
    };
  }
  return { visible: false, variant: 'waiting', message: '' };
};

// --- formData (viewMode) pour OperationFormulaireAdmin -------------------------
// OperationFormulaireAdmin attend une prop formData (OperationFormulaire acheteur). Cote
// presta, on lit commande.formulaire.* (buffer camelCase adaptApiData) avec fallback
// commande.operation.*. On enrichit delaisFinOperation depuis operation.delai_execution
// ou la difference debut/fin (parite buildConsultationFormData acheteur), pour afficher la
// duree d'execution. delaisConsultation = 10j (fixe pour les bons de commande).
export interface AdminViewFormData {
  nomOperation?: string;
  referenceInterne?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  coordonneesMaitriseDOuvrage?: string;
  coordonneesMaitrise?: string;
  besoin?: string;
  visitePreavis?: boolean;
  conditionVisite?: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
  delaisConsultation: number;
  delaisFinOperation: number;
}

const diffDays = (debut?: string, fin?: string): number | null => {
  if (!debut || !fin) return null;
  const d = new Date(debut);
  const f = new Date(fin);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return null;
  return Math.ceil(Math.abs(f.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export const buildAdminViewFormData = (commande: CommandeBCI): AdminViewFormData => {
  const f: CommandeFormulaire = commande.formulaire || {};
  const op: OperationRef = commande.operation || {};

  let delaisFinOperation = 30;
  const fromDates = diffDays(op.date_debut_operation, op.date_fin_operation);
  if (fromDates !== null) {
    delaisFinOperation = fromDates;
  }

  return {
    nomOperation: f.nomOperation ?? op.nom,
    referenceInterne: f.referenceInterne ?? op.reference,
    adresse: f.adresse ?? op.adresse,
    codePostal: f.codePostal ?? op.code_postal,
    ville: f.ville ?? op.ville,
    coordonneesMaitriseDOuvrage: f.coordonneesMaitriseDOuvrage ?? op.facturation,
    coordonneesMaitrise: f.coordonneesMaitrise ?? op.coordonnees_maitrise_ouvrage,
    besoin: f.besoin ?? op.besoin,
    visitePreavis: f.visitePreavis,
    conditionVisite: f.conditionVisite ?? op.condition_visite,
    dateDebutOperation: f.dateDebutOperation ?? op.date_debut_operation,
    dateFinOperation: f.dateFinOperation ?? op.date_fin_operation,
    delaisConsultation: 10,
    delaisFinOperation,
  };
};
